import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import AIChatWidget from './AIChatWidget';
import LanguagePrompt from './LanguagePrompt';
import { useLanguage } from '../hooks/useLanguage';

// JB Logo Component
const JBIcon = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_legal-consultations/artifacts/25luffpu_Untitled%20%28Logo%29%20%281%29.png"
    alt="J Brantley Law Logo"
    className="w-16 h-16 object-contain flex-shrink-0"
  />
);

// Language Indicator Component (EN and ES clickable)
const LanguageIndicator = () => {
  const { language, setLanguage } = useLanguage();
  
  return (
    <div className="flex items-center gap-1.5" data-testid="language-indicator">
      <button
        onClick={() => setLanguage('en')}
        className={`text-xs font-medium transition-colors ${
          language === 'en' ? 'text-white' : 'text-[#C2A46D] hover:text-white'
        }`}
        data-testid="lang-en"
      >
        EN
      </button>
      <span className="text-[#C2A46D]">|</span>
      <button
        onClick={() => setLanguage('es')}
        className={`text-xs font-medium transition-colors ${
          language === 'es' ? 'text-white' : 'text-[#C2A46D] hover:text-white'
        }`}
        data-testid="lang-es"
      >
        ES
      </button>
    </div>
  );
};

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Resources', href: '/resources' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();

  const translatedNavLinks = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.trademarks'), href: '/trademarks' },
    { name: t('nav.pricing'), href: '/pricing' },
    { name: t('nav.resources'), href: '/resources' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  return (
    <header className="bg-white border-b border-[#E8E4DC] sticky top-0 z-50">
      {/* Se Habla Español Banner */}
      <div className="bg-[#1F2A44] text-white py-1.5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setLanguage('es')}
              className="text-xs font-medium text-[#C2A46D] hover:text-white transition-colors cursor-pointer"
              data-testid="se-habla-espanol"
            >
              {t('common.seHablaEspanol')}
            </button>
            <LanguageIndicator />
          </div>
          <a 
            href="tel:+12107422435" 
            className="text-xs font-medium text-[#C2A46D] hover:text-white transition-colors"
            data-testid="header-phone"
          >
            Phone: (210) 742-2435
          </a>
        </div>
      </div>
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <JBIcon />
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-serif font-semibold text-[#1F2A44]">
                J. Brantley Law
              </span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#C2A46D]">
                The Fine Print Lawyer
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {translatedNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.href
                    ? 'text-[#1F2A44]'
                    : 'text-[#555555] hover:text-[#1F2A44]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/book" data-testid="header-book-btn">
              <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-6 py-2 rounded-md text-sm font-semibold">
                {t('nav.bookConsultation')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-[#1F2A44]" />
            ) : (
              <Menu className="h-6 w-6 text-[#1F2A44]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#E8E4DC]">
            <div className="flex flex-col gap-4">
              {translatedNavLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 ${
                    location.pathname === link.href
                      ? 'text-[#1F2A44]'
                      : 'text-[#555555]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/book" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] py-3 rounded-md text-sm font-semibold mt-2">
                  {t('nav.bookConsultation')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="bg-[#1F2A44] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-consultations/artifacts/25luffpu_Untitled%20%28Logo%29%20%281%29.png"
                alt="J Brantley Law Logo"
                className="w-16 h-16 object-contain flex-shrink-0"
              />
              <div className="flex flex-col">
                <span className="text-xl font-serif font-semibold text-white">
                  J. Brantley Law
                </span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#C2A46D]">
                  The Fine Print Lawyer
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              {t('footer.description')}
            </p>
            {/* Gold accent line */}
            <div className="w-12 h-0.5 bg-[#C2A46D] mt-4"></div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-[#C2A46D]">{t('footer.servicesTitle')}</h4>
            <ul className="space-y-3">
              <li><Link to="/services/trademark" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.trademarks.title')}</Link></li>
              <li><Link to="/services/business-formation" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.businessFormation.title')}</Link></li>
              <li><Link to="/services/contracts" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.contracts.title')}</Link></li>
              <li><Link to="/services/government-contracts" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.governmentContracts.title')}</Link></li>
              <li><Link to="/services/personal-injury" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.personalInjury.title')}</Link></li>
              <li><Link to="/family-legal-planning" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('services.familyLegalPlanning.title')}</Link></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-[#C2A46D]">{t('footer.quickLinksTitle')}</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('footer.aboutJennifer')}</Link></li>
              <li><Link to="/pricing" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('nav.pricing')}</Link></li>
              <li><Link to="/resources" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('nav.resources')}</Link></li>
              <li><Link to="/contact" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('nav.contact')}</Link></li>
              <li><Link to="/book" className="text-sm text-gray-400 hover:text-[#C2A46D] transition-colors">{t('footer.bookConsultation')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-[#C2A46D]">{t('footer.contactTitle')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#C2A46D] mt-0.5" />
                <a href="mailto:jbrantley@jenniferbrantleylaw.com" className="text-sm text-gray-400 hover:text-white transition-colors">
                  jbrantley@jenniferbrantleylaw.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C2A46D] mt-0.5" />
                <span className="text-sm text-gray-400">
                  {t('footer.servingClients')}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-xs text-gray-500 hover:text-[#C2A46D] transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <Link to="/terms" className="text-xs text-gray-500 hover:text-[#C2A46D] transition-colors">
                {t('footer.termsOfService')}
              </Link>
            </div>
          </div>
          <p className="text-[10px] text-gray-600 mt-4 text-center md:text-left">
            {t('footer.disclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFCFA]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <AIChatWidget />
      <LanguagePrompt />
    </div>
  );
};

export default Layout;
